import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { useEffect, useState } from "react";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import { SectionNav } from "@/components/section-nav";
import { supabase } from "@/integrations/supabase/client";

type Episode = {
  id: string;
  title: string;
  subtitle: string | null;
  episode_number: number | null;
  description: string | null;
  spotify_embed_html: string | null;
  youtube_embed_html: string | null;
  cover_image_url: string | null;
  published_at: string | null;
};

type EpisodeMeta = {
  title: string | null;
  subtitle: string | null;
  description: string | null;
  cover_image_url: string | null;
} | null;

const fetchEpisodeMeta = createServerFn({ method: "GET" })
  .inputValidator((data: { id: string }) => data)
  .handler(async ({ data }): Promise<EpisodeMeta> => {
    const supa = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: row } = await supa
      .from("podcast_episodes" as never)
      .select("title,subtitle,description,cover_image_url")
      .eq("id", data.id)
      .maybeSingle();
    return (row as EpisodeMeta) ?? null;
  });

export const Route = createFileRoute("/comunicaciones/podcast/$id")({
  loader: ({ params }) => fetchEpisodeMeta({ data: { id: params.id } }),
  head: ({ params, loaderData }) => {
    const ep = loaderData;
    const title = ep?.title ?? "Episodio";
    const desc =
      ep?.subtitle ?? ep?.description?.slice(0, 200) ?? "Historias que Escalan, el podcast de Mine Class.";
    const url = `https://mine-class.com/comunicaciones/podcast/${params.id}`;
    return {
      meta: [
        { title: `${title} | Historias que Escalan | Mine Class` },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        ...(ep?.cover_image_url ? [{ property: "og:image", content: ep.cover_image_url }] : []),
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: Page,
});

function Page() {
  const { id } = Route.useParams();
  const [ep, setEp] = useState<Episode | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("podcast_episodes" as never)
        .select("id,title,subtitle,episode_number,description,spotify_embed_html,youtube_embed_html,cover_image_url,published_at")
        .eq("id", id)
        .maybeSingle();
      setEp((data ?? null) as Episode | null);
      setLoading(false);
    })();
  }, [id]);

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <SiteNav />
      <SectionNav section="comunicaciones" />
      <main className="flex-1 max-w-4xl mx-auto px-margin-desktop py-xl w-full">
        <Link to="/comunicaciones/podcast" className="text-sm underline text-[var(--color-brand-purple)]">
          ← Volver a Historias que Escalan
        </Link>
        {loading ? (
          <p className="mt-8 text-[var(--color-on-surface-variant)]">Cargando…</p>
        ) : !ep ? (
          <p className="mt-8 text-[var(--color-on-surface-variant)]">Episodio no encontrado.</p>
        ) : (
          <article className="mt-6">
            <div className="grid md:grid-cols-[240px_1fr] gap-8">
              {ep.cover_image_url && (
                <img src={ep.cover_image_url} alt={ep.title} className="w-full aspect-square object-cover rounded" />
              )}
              <div>
                {ep.episode_number != null && (
                  <span className="text-xs uppercase tracking-widest text-[var(--color-brand-coral)] font-bold">
                    Episodio #{ep.episode_number}
                  </span>
                )}
                <h1 className="text-4xl font-bold text-[var(--color-brand-purple)] mt-2">{ep.title}</h1>
                {ep.subtitle && <p className="text-lg text-[var(--color-on-surface-variant)] mt-2">{ep.subtitle}</p>}
                {ep.published_at && (
                  <p className="text-sm text-[var(--color-on-surface-variant)] mt-3">
                    {new Date(ep.published_at).toLocaleDateString("es-CL", { dateStyle: "long" })}
                  </p>
                )}
              </div>
            </div>
            {ep.description && <p className="mt-8 text-base whitespace-pre-wrap">{ep.description}</p>}
            {(ep.spotify_embed_html || ep.youtube_embed_html) && (
              <div className="grid md:grid-cols-2 gap-4 mt-8">
                {ep.spotify_embed_html && (
                  <div className="rounded-lg overflow-hidden" dangerouslySetInnerHTML={{ __html: ep.spotify_embed_html }} />
                )}
                {ep.youtube_embed_html && (
                  <div className="rounded-lg overflow-hidden aspect-video" dangerouslySetInnerHTML={{ __html: ep.youtube_embed_html }} />
                )}
              </div>
            )}
          </article>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
